import { useStore } from 'hooks/useStore';
import { CreateTicketPayload } from 'Interfaces/SupportTickets/supportTicketInterfaces';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FormEvent, useState, useEffect } from 'react';
import { captureComprehensiveTicketData } from 'utils/captureTicketData';
import classes from './supportTicketChat.module.scss';

const SupportTicketChat = () => {
  const {
    SupportTicketStore: {
      isChatOpen,
      closeChat,
      categories,
      isCategoriesLoading,
      isCreatingTicket,
      formData,
      setFormData,
      createJSMTicket,
      fetchCategories
    },
    UserStore: { userMetadata }
  } = useStore();

  // Fetch categories when chat opens
  useEffect(() => {
    if (isChatOpen && categories.length === 0 && !isCategoriesLoading) {
      fetchCategories();
    }
  }, [isChatOpen, categories.length, isCategoriesLoading, fetchCategories]);

  const getEnvironment = (): string => {
    const origin = window.location.origin;
    if (origin.includes('localhost')) {
      return 'local';
    }
    if (origin.includes('dev')) {
      return 'dev';
    }
    if (origin.includes('staging')) {
      return 'staging';
    }
    {
      return 'production';
    }
  };

  const [attachments, setAttachments] = useState<File[]>([]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.category || !formData.title || !formData.description) {
      return;
    }

    try {
      // Capture comprehensive ticket data (custom context included in additionalContext)
      const { browserInfo, additionalContext } = await captureComprehensiveTicketData({
        userId: userMetadata?.userInfo?.id,
        userEmail: userMetadata?.userInfo?.email,
        userName: userMetadata?.userInfo?.name ?? '',
        userRole: userMetadata?.roles?.pps?.[0]?.roleId?.roleCode ?? 'N/A',
        department: userMetadata?.userInfo?.deptId?.departmentCode,
        environment: getEnvironment(),
        sessionActive: 'Yes'
      });

      const payload: CreateTicketPayload = {
        category: formData.category,
        title: formData.title,
        description: formData.description,
        attachments,
        browserInfo,
        additionalContext
      };

      await createJSMTicket(payload);

      // Reset attachments
      setAttachments([]);
    } catch (error) {
      console.error('Error submitting ticket:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  if (!isChatOpen) return null;

  const categoriesJS = toJS(categories);

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className={classes.backdrop}
        onClick={closeChat}
        aria-hidden="true"
      />
      
      {/* Popover */}
      <div className={classes.popoverContainer}>
      {/* Header */}
      <div className={classes.header}>
        <h2>Report an Issue</h2>
        <Button
          icon="pi pi-times"
          className="p-button-text p-button-rounded p-button-sm"
          onClick={closeChat}
          aria-label="Close"
        />
      </div>

      {/* Content */}
      <div className={classes.content}>
        <form onSubmit={handleSubmit}>
          {/* Category */}
          <div className={classes.field}>
            <label htmlFor="category">Category *</label>
            {isCategoriesLoading ? (
              <div>
                <ProgressSpinner style={{ width: '24px', height: '24px' }} />
              </div>
            ) : (
              <Dropdown
                id="category"
                value={formData.category || null}
                options={categoriesJS}
                onChange={e => setFormData({ category: e.value })}
                optionLabel="category_name"
                optionValue="id"
                placeholder="Select a category"
                className={classes.dropdown}
                emptyMessage="No categories available"
                filter
                panelStyle={{ zIndex: 9999 }}
                appendTo="self"
              />
            )}
          </div>

          {/* Title */}
          <div className={classes.field}>
            <label htmlFor="title">Title *</label>
            <InputText
              id="title"
              value={formData.title}
              onChange={e => setFormData({ title: e.target.value })}
              placeholder="Brief summary of the issue"
              className={classes.input}
              required
            />
          </div>

          {/* Description */}
          <div className={classes.field}>
            <label htmlFor="description">Description *</label>
            <InputTextarea
              id="description"
              value={formData.description}
              onChange={e => setFormData({ description: e.target.value })}
              placeholder="Detailed description of the issue"
              rows={4}
              className={classes.textarea}
              required
            />
          </div>

          {/* Attachments */}
          <div className={classes.field}>
            <label htmlFor="attachments">Attachments (optional)</label>
            <input
              id="attachments"
              type="file"
              multiple
              onChange={handleFileChange}
              className={classes.fileInput}
            />
            {attachments.length > 0 && (
              <div className={classes.attachmentsList}>
                {attachments.map((file, index) => (
                  <div key={index} className={classes.attachmentItem}>
                    <span className={classes.fileName}>{file.name}</span>
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-sm"
                      onClick={() => removeAttachment(index)}
                      type="button"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auto-filled info */}
          <div className={classes.autoFilledInfo}>
            <p>
              <strong>Page:</strong> {formData.currentPage || window.location.href}
            </p>
            <p>
              <strong>Environment:</strong> {getEnvironment()}
            </p>
          </div>

          {/* Submit Button */}
          <div className={classes.actions}>
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm"
              onClick={closeChat}
              disabled={isCreatingTicket}
            />
            <Button
              type="submit"
              label="Submit"
              icon={isCreatingTicket ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
              disabled={
                isCreatingTicket || !formData.category || !formData.title || !formData.description
              }
              size="small"
            />
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default observer(SupportTicketChat);
